import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  Alert,
  Progress,
  Card,
  CardBody,
} from "reactstrap";
import { toast } from "react-toastify";
import FeatherIcon from "feather-icons-react";
import { APIClient, getLoggedinUser } from "../../helpers/api_helper";

const apiClient = new APIClient();

const ModalImportCSV = ({ isOpen, toggle, onImportSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [userData, setUserData] = useState({});

  useEffect(() => {
    const getData = async () => {
      const user = await getLoggedinUser();
      setUserData(user);
    };
    getData();
  }, []);

  const resetModal = () => {
    setSelectedFile(null);
    setImportResults(null);
    setDragOver(false);
  };

  const handleFileSelect = (file) => {
    // Validate file type
    const allowedTypes = ['.csv', '.txt'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!allowedTypes.includes(fileExtension)) {
      toast.error("Tipo de arquivo inválido. Apenas arquivos .csv e .txt são permitidos.");
      return;
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      toast.error("Arquivo muito grande. O tamanho máximo permitido é 10MB.");
      return;
    }

    setSelectedFile(file);
    setImportResults(null);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      toast.error("Por favor, selecione um arquivo CSV.");
      return;
    }

    // Check admin permission
    if (userData?.data?.role !== "Admin") {
      toast.error("Acesso negado. Apenas administradores podem importar arquivos CSV.");
      return;
    }

    setLoading(true);
    setImportResults(null);

    try {
      const formData = new FormData();
      formData.append('arquivo_csv', selectedFile);

      const response = await apiClient.post('/notas/import-csv', formData);

      if (response.sucesso) {
        setImportResults(response.dados);
        
        // Check if there are any errors
        const hasErrors = response.dados.erros && response.dados.erros > 0;
        
        if (!hasErrors) {
          // Complete success - show success message and auto-close
          toast.success(response.mensagem || "Importação realizada com sucesso!");
          setTimeout(() => {
            if (onImportSuccess) {
              onImportSuccess();
            }
          }, 2000);
        } else {
          // Partial success with errors - keep modal open
          const successCount = response.dados.sucessos || 0;
          const errorCount = response.dados.erros || 0;
          toast.warning(`Importação concluída: ${successCount} sucesso(s), ${errorCount} erro(s). Verifique os detalhes abaixo.`);
        }
      } else {
        toast.error(response.mensagem || "Erro ao importar arquivo CSV.");
      }
    } catch (error) {
      toast.error("Erro ao processar arquivo CSV.");
      console.error("Import error:", error);
    }
    
    setLoading(false);
  };

  const handleClose = () => {
    resetModal();
    toggle();
  };

  const handleTryAgain = () => {
    setSelectedFile(null);
    setImportResults(null);
    // Keep modal open for user to select a new file
  };

  const downloadSample = () => {
    const link = document.createElement('a');
    link.href = '/sample-notas-fiscais.csv';
    link.download = 'exemplo-notas-fiscais.csv';
    link.click();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <Form onSubmit={handleSubmit}>
        <ModalHeader toggle={handleClose}>
          <FeatherIcon icon="upload" className="me-2" />
          Importar Notas Fiscais via CSV
        </ModalHeader>
        
        <ModalBody>
          {/* Instructions */}
          <Alert color="info" className="mb-4">
            <h6 className="mb-2">
              <FeatherIcon icon="info" className="me-1" />
              Instruções para Importação
            </h6>
            <ul className="mb-2">
              <li>O arquivo deve estar no formato CSV com separador ponto e vírgula (;)</li>
              <li>Cabeçalho obrigatório: <code>Nome;CPF / CNPJ;Valor (R$);Data</code></li>
              <li>Formato da data: DD/MM/AAAA (ex: 25/12/2024)</li>
              <li>Formato do valor: use vírgula como decimal (ex: 1.250,50)</li>
              <li>Tamanho máximo: 10MB</li>
            </ul>
            <Button 
              color="link" 
              className="p-0 text-decoration-underline"
              type="button"
              onClick={downloadSample}
            >
              <FeatherIcon icon="download" className="me-1" size={14} />
              Baixar arquivo de exemplo
            </Button>
          </Alert>

          {/* File Upload Area */}
          <FormGroup>
            <Label>Arquivo CSV</Label>
            <div
              className={`border-2 border-dashed rounded p-4 text-center ${
                dragOver ? 'border-primary bg-light' : 'border-secondary'
              } ${selectedFile ? 'bg-success-subtle' : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              style={{ minHeight: '120px', cursor: 'pointer' }}
              onClick={() => document.getElementById('fileInput').click()}
            >
              <Input
                type="file"
                id="fileInput"
                accept=".csv,.txt"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />
              
              {selectedFile ? (
                <div>
                  <FeatherIcon icon="file-text" size={32} className="text-success mb-2" />
                  <div className="fw-semibold text-success">{selectedFile.name}</div>
                  <div className="text-muted small">
                    {formatFileSize(selectedFile.size)}
                  </div>
                  <Button
                    color="link"
                    size="sm"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                    }}
                    className="text-danger mt-2"
                  >
                    <FeatherIcon icon="x" size={14} className="me-1" />
                    Remover arquivo
                  </Button>
                </div>
              ) : (
                <div>
                  <FeatherIcon icon="upload-cloud" size={32} className="text-muted mb-2" />
                  <div>Arraste e solte o arquivo CSV aqui</div>
                  <div className="text-muted small">ou clique para selecionar</div>
                </div>
              )}
            </div>
          </FormGroup>

          {/* Import Results */}
          {importResults && (
            <Card className={`mt-4 ${importResults.erros > 0 ? 'border-warning' : 'border-success'}`}>
              <CardBody>
                <h6 className="mb-3">
                  <FeatherIcon 
                    icon={importResults.erros > 0 ? "alert-triangle" : "check-circle"} 
                    className={`me-2 ${importResults.erros > 0 ? 'text-warning' : 'text-success'}`} 
                  />
                  Resultado da Importação
                </h6>
                
                <div className="row mb-3">
                  <div className="col-6">
                    <div className="text-center p-3 bg-success-subtle rounded">
                      <div className="fw-bold text-success fs-4">{importResults.sucessos || 0}</div>
                      <div className="small text-muted">Sucessos</div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-center p-3 bg-danger-subtle rounded">
                      <div className="fw-bold text-danger fs-4">{importResults.erros || 0}</div>
                      <div className="small text-muted">Erros</div>
                    </div>
                  </div>
                </div>

                {/* Success message for complete success */}
                {(!importResults.erros || importResults.erros === 0) && (
                  <Alert color="success" className="small mb-3">
                    <FeatherIcon icon="check" size={14} className="me-1" />
                    Todas as notas fiscais foram importadas com sucesso! A lista será atualizada automaticamente.
                  </Alert>
                )}

                {importResults.detalhes_erros && importResults.detalhes_erros.length > 0 && (
                  <div>
                    <h6 className="text-danger mb-2">Detalhes dos Erros:</h6>
                    <Alert color="warning" className="small mb-3">
                      <FeatherIcon icon="info" size={14} className="me-1" />
                      Corrija os erros no arquivo CSV e tente importar novamente. Use o botão "Tentar Novamente" abaixo.
                    </Alert>
                    <div className="bg-light p-3 rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {importResults.detalhes_erros.map((erro, index) => (
                        <div key={index} className="small mb-1 text-danger">
                          <FeatherIcon icon="alert-circle" size={12} className="me-1" />
                          {erro}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          )}
        </ModalBody>
        
        <ModalFooter>
          <Button color="secondary" onClick={handleClose} disabled={loading}>
            {importResults ? 'Fechar' : 'Cancelar'}
          </Button>
          
          {/* Show import button when no results yet */}
          {!importResults && (
            <Button 
              color="primary" 
              type="submit" 
              disabled={loading || !selectedFile}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Importando...
                </>
              ) : (
                <>
                  <FeatherIcon icon="upload" className="me-2" />
                  Importar Arquivo
                </>
              )}
            </Button>
          )}
          
          {/* Show try again button when there are errors */}
          {importResults && importResults.erros > 0 && (
            <Button 
              color="warning" 
              onClick={handleTryAgain}
              disabled={loading}
            >
              <FeatherIcon icon="refresh-cw" className="me-2" />
              Tentar Novamente
            </Button>
          )}
          
          {/* Show refresh button for complete success (optional - will auto-close) */}
          {importResults && (!importResults.erros || importResults.erros === 0) && (
            <Button 
              color="success" 
              onClick={() => {
                if (onImportSuccess) {
                  onImportSuccess();
                }
              }}
              disabled={loading}
            >
              <FeatherIcon icon="check" className="me-2" />
              Atualizar Lista
            </Button>
          )}
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default ModalImportCSV; 